import React from 'react';
import { useNavigate, useRevalidator } from 'react-router-dom';

import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';

import {
  createHtmlDocumentTemplateFromStarter,
  getHtmlDocumentTemplateStarters,
  type HtmlDocumentTemplateStarter
} from '~/api/data/html-templates/starters';

const errorMessage = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

export function StarterTemplatesDialog(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const [starters, setStarters] = React.useState<HtmlDocumentTemplateStarter[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [pendingId, setPendingId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setError(null);
    getHtmlDocumentTemplateStarters()
      .then((result) => {
        if (cancelled) return;
        if (result.error) throw result.error;
        setStarters(result.data ?? []);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errorMessage(e, 'Starter templates could not be loaded.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleClose = () => {
    // Closing mid-create would strand the navigation to the new template.
    if (pendingId) return;
    onClose();
  };

  const choose = async (starterId: string) => {
    setError(null);
    setPendingId(starterId);
    try {
      const result = await createHtmlDocumentTemplateFromStarter(starterId);
      if (result.error) throw result.error;
      const id = result.data?.createHtmlDocumentTemplateFromStarter.id;
      if (!id) throw new Error('The template was not created.');
      void revalidator.revalidate();
      setPendingId(null);
      onClose();
      navigate(`/app/library/templates/${id}`);
    } catch (e) {
      setPendingId(null);
      setError(errorMessage(e, 'The template could not be created.'));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} size="2xl">
      <DialogTitle>Start from a starter</DialogTitle>
      <DialogDescription>
        Adding a starter puts a copy in your templates right away.
      </DialogDescription>
      <DialogBody>
        {loading && <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading…</p>}
        {loadError && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {loadError}
          </p>
        )}
        {!loading && !loadError && starters.length === 0 && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            There are no starter templates yet.
          </p>
        )}
        <ul className="divide-y divide-neutral-200 dark:divide-neutral-700">
          {starters.map((starter) => (
            <li key={starter.id} className="flex items-start gap-4 py-4">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-neutral-900 dark:text-white">
                  {starter.title}
                </div>
                <p className="mt-0.5 text-sm text-neutral-600 dark:text-neutral-400">
                  {starter.description}
                </p>
              </div>
              <Button
                type="button"
                color="sky"
                loading={pendingId === starter.id}
                disabled={pendingId !== null}
                onClick={() => void choose(starter.id)}
                aria-label={`Add ${starter.title} to my templates`}
              >
                Add to my templates
              </Button>
            </li>
          ))}
        </ul>
        {error && (
          <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </DialogBody>
      <DialogActions>
        <Button type="button" plain onClick={handleClose} disabled={pendingId !== null}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
